import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, Trash2, Eye, Filter, Download, FileSpreadsheet, Store, MessageCircle, CheckCircle, MessageSquare } from "lucide-react";

export default function History() {
  const { token } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [storeFilter, setStoreFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all"); // all, manual, whatsapp
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchAnalyses();
  }, [selectedCategory, storeFilter, sourceFilter]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchAnalyses = async () => {
    setLoading(true);
    try {
      let params = [];
      if (selectedCategory !== "all") params.push(`category_id=${selectedCategory}`);
      if (storeFilter.trim()) params.push(`store_name=${encodeURIComponent(storeFilter.trim())}`);
      if (sourceFilter !== "all") params.push(`source=${sourceFilter}`);
      const queryString = params.length > 0 ? `?${params.join('&')}` : "";
      
      const response = await axios.get(`${API}/analysis${queryString}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalyses(response.data.analyses);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Erro ao carregar histórico");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta análise?")) return;
    
    try {
      await axios.delete(`${API}/analysis/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Análise excluída");
      fetchAnalyses();
    } catch (error) {
      toast.error("Erro ao excluir análise");
    }
  };

  const handleExport = async () => {
    try {
      const params = selectedCategory !== "all" ? `?category_id=${selectedCategory}` : "";
      const response = await axios.get(`${API}/reports/export${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create CSV
      const csvContent = [
        ["Data", "Loja", "Categoria", "Classificação", "Ocupação (%)", "Prateleiras Vazias", "Detalhes"].join(","),
        ...response.data.report.map(a => [
          new Date(a.created_at).toLocaleDateString('pt-BR'),
          `"${(a.store_name || '').replace(/"/g, '""')}"`,
          a.category_name,
          a.classification || '',
          a.occupancy_percentage,
          a.empty_shelves_count || 0,
          `"${(a.analysis_details || '').replace(/"/g, '""')}"`
        ].join(","))
      ].join("\n");

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `shelfvision-report-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success("Relatório CSV exportado!");
    } catch (error) {
      toast.error("Erro ao exportar relatório");
    }
  };

  const handleExportExcel = async () => {
    try {
      let params = [];
      if (selectedCategory !== "all") params.push(`category_id=${selectedCategory}`);
      if (storeFilter.trim()) params.push(`store_name=${encodeURIComponent(storeFilter.trim())}`);
      if (sourceFilter !== "all") params.push(`source=${sourceFilter}`);
      const queryString = params.length > 0 ? `?${params.join('&')}` : "";
      
      const response = await axios.get(`${API}/reports/export/excel${queryString}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `shelfvision-relatorio-${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success("Relatório Excel exportado!");
    } catch (error) {
      toast.error("Erro ao exportar Excel");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOccupancyColor = (value) => {
    if (value >= 80) return "#34C759";
    if (value >= 50) return "#FF9500";
    return "#FF3B30";
  };

  const getClassificationColor = (classification) => {
    switch (classification) {
      case 'BOA': return "#34C759";
      case 'RUIM': return "#FF9500";
      case 'VAZIA': return "#FF3B30";
      case 'CRITICA': return "#8B0000";
      default: return "#6B7280";
    }
  };

  const getClassificationLabel = (classification) => {
    switch (classification) {
      case 'BOA': return "Boa";
      case 'RUIM': return "Ruim";
      case 'VAZIA': return "Vazia";
      case 'CRITICA': return "Crítica";
      default: return classification || "—";
    }
  };

  return (
    <Sidebar>
      <div className="p-6 lg:p-8" data-testid="history-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-chivo font-bold text-3xl tracking-tight mb-1">Histórico</h1>
            <p className="text-zinc-500 text-sm">{total} análises encontradas</p>
          </div>
          <Button
            onClick={handleExportExcel}
            data-testid="export-excel-btn"
            className="h-10 px-5 rounded-none bg-green-600 hover:bg-green-700 text-white gap-2"
          >
            <FileSpreadsheet size={18} strokeWidth={1.5} />
            Exportar Excel
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white border border-zinc-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <Filter size={18} className="text-zinc-400" />
            <Input
              value={storeFilter}
              onChange={(e) => setStoreFilter(e.target.value)}
              placeholder="Filtrar por loja..."
              className="w-[200px] h-10 rounded-none"
              data-testid="filter-store"
            />
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="filter-category" className="w-[200px] h-10 rounded-none">
                <SelectValue placeholder="Filtrar por categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as categorias</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger data-testid="filter-source" className="w-[180px] h-10 rounded-none">
                <SelectValue placeholder="Origem" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as origens</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent animate-spin rounded-full" />
          </div>
        ) : analyses.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-12 text-center">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-zinc-300" />
            <p className="text-zinc-500 mb-4">Nenhuma análise encontrada</p>
            <Link to="/new-analysis">
              <Button className="h-10 px-5 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white">
                Criar Nova Análise
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {analyses.map((analysis) => (
              <div 
                key={analysis.id}
                className="bg-white border border-zinc-200 p-5 hover:border-zinc-300 transition-colors"
                data-testid={`analysis-item-${analysis.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      {analysis.source === 'whatsapp' && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-green-100 text-green-700">
                          <MessageCircle size={12} />
                          WhatsApp
                        </span>
                      )}
                      {/* Indicador de Feedback */}
                      {analysis.user_feedback ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-blue-100 text-blue-700" title={`Feedback: ${analysis.user_feedback}`}>
                          <CheckCircle size={12} />
                          Feedback
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-amber-50 text-amber-600 border border-amber-200" title="Sem feedback">
                          <MessageSquare size={12} />
                          Sem Feedback
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-zinc-100">
                        <Store size={12} />
                        {analysis.store_name || 'Sem loja'}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-zinc-50 border border-zinc-200">
                        {analysis.category_name}
                      </span>
                      <span className="text-xs text-zinc-400 flex items-center gap-1">
                        <Calendar size={12} />
                        {formatDate(analysis.created_at)}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-600 line-clamp-1">
                      {analysis.analysis_details}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-6 ml-4">
                    <div className="text-right">
                      <p className="text-xs text-zinc-400 mb-1">Classificação</p>
                      <p 
                        className="font-chivo font-bold text-xl"
                        style={{ color: getClassificationColor(analysis.classification) }}
                      >
                        {getClassificationLabel(analysis.classification)}
                      </p>
                    </div>
                    <div className="text-right border-l border-zinc-200 pl-4">
                      <p className="text-xs text-zinc-400 mb-1">Ocupação</p>
                      <p 
                        className="font-chivo font-bold text-xl"
                        style={{ color: getOccupancyColor(analysis.occupancy_percentage) }}
                      >
                        {analysis.occupancy_percentage}%
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-1 border-l border-zinc-200 pl-4">
                      <Link to={`/analysis/${analysis.id}`}>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-9 w-9 hover:bg-zinc-100"
                          data-testid={`view-analysis-${analysis.id}`}
                        >
                          <Eye size={18} strokeWidth={1.5} />
                        </Button>
                      </Link>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDelete(analysis.id)}
                        className="h-9 w-9 hover:bg-red-50 hover:text-red-600"
                        data-testid={`delete-analysis-${analysis.id}`}
                      >
                        <Trash2 size={18} strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Sidebar>
  );
}
